import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';

import { List } from 'antd';

import useDimensions from '@hooks/Layout/UseDimensions';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import { useContactsSelector } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ModelCard from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsCards/ModelCard';
import ModelCardPackage from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsCards/ModelCardPackage';
import useFilteredFeatureIDs from '@components/ContactsSelector/ModelsSelector/ModelsWall/UseFilteredFeatureIDs';
import CenteredBlock from '@components/Gui/CenteredBlock';
import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';

import { WallContainer } from './ModelCardUI';

const ModelCardsWrapper = ({ footer, isLoading, models, modelEventsInfo }) => {
    const { context } = useContactsSelector();
    const { scope } = context;
    const filteredFeatureIDs = useFilteredFeatureIDs();

    const [ref, { width }] = useDimensions();
    const [pagedList, setPagedList] = useState([]);
    const [loadMore, setLoadMore] = useState(false);

    const usePagination = true;

    const pageCardCount = usePagination ? 50 : models.length;

    // Seleziona il componente card giusto in base allo scope
    const CardComponent = scope === 'package' ? ModelCardPackage : ModelCard;

    // Calcola dimensione colonne in base al tipo di card
    const cardWidth = scope === 'package' ? 190 : 250;

    useEffect(() => {
        setPagedList(models.slice(0, pageCardCount));
    }, [models]);

    const loader = useRef(null);

    const handleObserver = useCallback((entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setLoadMore(true);
        }
    }, []);

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '10px',
            threshold: 0,
        };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    useEffect(() => {
        setPagedList(models.slice(0, pageCardCount));
    }, [models]);

    useEffect(() => {
        if (loadMore) {
            if (models.length > pagedList.length) {
                const newShowedList = models.slice(pagedList.length, pagedList.length + pageCardCount);
                setPagedList([...pagedList, ...newShowedList]);
            }
            setLoadMore(false);
        }
    }, [loadMore]);

    return (
        <>
            <WallContainer ref={ref}>
                {isLoading ? (
                    <Loading loadingType="centered" />
                ) : models.length ? (
                    <>
                        <List
                            style={{ padding: '1rem 0' }}
                            grid={{
                                gutter: 8,
                                column: Math.floor(width / cardWidth),
                            }}
                            dataSource={pagedList}
                            renderItem={(model) => {
                                return (
                                    <List.Item key={model.ID}>
                                        <CardComponent
                                            model={model}
                                            modelEventsInfo={modelEventsInfo}
                                            highlightedFeatureIDs={filteredFeatureIDs}
                                        />
                                    </List.Item>
                                );
                            }}
                        />
                    </>
                ) : (
                    <CenteredBlock>
                        <EmptyState message="no models fitting your search" />
                    </CenteredBlock>
                )}
                <div ref={loader}>{loadMore && <Loading textBlack small />}</div>
            </WallContainer>
            {footer}
        </>
    );
};

export default ModelCardsWrapper;
